/**
 * enginePreProcess_Helper.ts - Helper functions for EnginePreProcess
 * Port of C# EnginePreProcess_Helper.cs
 * Pure ES Module version
 */

import { JsonMergeUtil } from '../../base/engine/jsonMergeUtil.js';
import { OperationsMetadata, PlaceholderIndex } from '../../model/index.js';
import { PreprocessedTemplate } from '../model/index.js';
import type { EnginePreProcess } from './enginePreProcess.js';
import type { JsonObject } from '../../app/json/jsonValue.js';
import type { TemplateOperation } from '../../model/templateOperation.js';
import type { InheritanceOperation } from '../../model/inheritanceOperation.js';
import type { PhaseTiming } from '../../base/timing/phaseTiming.js';
import { TimingPhase } from '../../base/timing/timingPhase.js';

/**
 * Create flow stack for inheritance resolution
 */
export function createFlowStack(appFile: string): string[] {
    return [appFile];
}

/**
 * Get main template from loader
 */
export function getMainTemplate(engine: EnginePreProcess, appSite: string, appFile: string, appView: string | null, flowStack: string[], timing: PhaseTiming, configJson: JsonObject | null = null, clientMode: boolean = false): { mainTemplate: PreprocessedTemplate; mainJson: JsonObject | null; resolvedTemplateName: string; resolvedAppSite: string; mainLatePlaceholders: string[] } {
    return timing.measure(TimingPhase.Bind, () => {
    const { template: mainTemplate, json: mainJson, resolvedTemplateName, resolvedAppSite, viewHtml: wrappedHtml, latePlaceholders: mainLatePlaceholders } =
        engine._loader!.getTemplateWithView(appSite, appFile, appFile, timing, appView, flowStack, configJson, clientMode);

    // Create new template with wrapped HTML (preserves preprocessed data, keeps cache immutable)
    const resultTemplate = new PreprocessedTemplate();
    resultTemplate.originalContent = wrappedHtml;
    resultTemplate.actualTemplateName = mainTemplate.actualTemplateName;
    resultTemplate.placeholders = mainTemplate.placeholders;
    resultTemplate.slottedTemplates = mainTemplate.slottedTemplates;
    resultTemplate.replacementMappings = mainTemplate.replacementMappings;
    resultTemplate.jsonArrayPlaceholders = mainTemplate.jsonArrayPlaceholders;

    return { mainTemplate: resultTemplate, mainJson, resolvedTemplateName, resolvedAppSite, mainLatePlaceholders };
    });
}

/**
 * Merge main template with JSON
 */
export function mergeMainTemplateJson(engine: EnginePreProcess, mainTemplate: PreprocessedTemplate, mainJson: JsonObject | null, contentHtml: string, templateKey: string, appFile: string, operations: TemplateOperation[], contextJson: JsonObject | null, configJson: JsonObject | null, timing: PhaseTiming): string {
    return timing.measure(TimingPhase.Bind, () => {
    if (!mainJson) {
        return contentHtml;
    }

    const placeholderIndex = PlaceholderIndex.fromParsed(mainTemplate.jsonArrayPlaceholders, mainTemplate.jsonKeyPlaceholders);
    const merged = JsonMergeUtil.mergeTemplateWithJson(
        contentHtml,
        mainJson,
        templateKey,
        appFile,
        operations,
        engine._loader!.maxArrayOperationItems,
        engine._loader!.defaultReplaceHtml,
        engine._loader!.defaultSkipJson,
        placeholderIndex,
        contextJson,
        configJson
    );

    return merged;
    });
}

/**
 * Build operations metadata
 */
export function buildOperationsMetadata(genOperationJson: boolean, appSite: string, appFile: string, appView: string | null, templateKey: string, operations: TemplateOperation[], inheritanceOps: InheritanceOperation[], timing: PhaseTiming): OperationsMetadata | null {
    return timing.measure(TimingPhase.Assemble, () => {
    if (!genOperationJson) {
        return null;
    }

    const opsMetadata = new OperationsMetadata();
    opsMetadata.appSite = appSite;
    opsMetadata.appFile = appFile;
    opsMetadata.appView = appView || '';
    opsMetadata.templateName = templateKey;
    opsMetadata.operations = operations;
    opsMetadata.hasInheritance = inheritanceOps.length > 0;
    opsMetadata.inheritanceOperations = inheritanceOps;

    return opsMetadata;
    });
}
